import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatInstant } from '../../utils/datetime';
import {
  applyPolicyPrices,
  discardPolicyPreview,
  fetchManapoolListingSyncStatus,
  fetchManapoolMarketIntelStatus,
  fetchPolicyPricingPreviewRows,
  fetchPolicyPricingPreviewStatus,
  fetchPolicyPricingStatus,
  fetchPreferredRefreshStatus,
  fetchTcgplayerPricingExportStatus,
  fetchTcgplayerSellerSessionStatus,
  isAsyncJobActive,
  isAsyncJobFailed,
  isAsyncJobSuccess,
  pollAsyncJobStatus,
  previewPolicyPrices,
  rebaseAcquisitionPackCosts,
  refreshPreferredPrices,
  runTcgplayerPricingExport,
  syncManapoolListingPrices,
  syncManapoolMarketIntel,
  type AcquisitionPackRebaseResult,
  type AsyncJobStatus,
  type PolicyApplyAdjustment,
  type PolicyPreviewRowsPage,
  type TcgplayerSellerSessionStatus,
} from '../../api/pricing';
import { colors, spacing, typography, typographyFlat, borderRadius } from '../../styles/tokens';
import { PolicyPreviewPanel } from './PolicyPreviewPanel';

type JobKey = 'policyPreview' | 'policy' | 'preferred' | 'listing' | 'market' | 'tcgExport';
type ActionKey = JobKey | 'tcgStatus';

const MARKET_INTEL_PRODUCT_LIMIT = 2000;

const JOB_LABELS: Record<JobKey, string> = {
  policyPreview: 'Policy preview',
  policy: 'Policy apply',
  preferred: 'Preferred refresh',
  listing: 'Listing sync',
  market: 'Market intel',
  tcgExport: 'TCG pricing export',
};

function formatApiError(err: unknown): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
      const nested = detail as Record<string, unknown>;
      if (typeof nested.stale_reason === 'string') return nested.stale_reason;
      if (Array.isArray(nested.errors) && nested.errors.length) {
        return nested.errors.map(String).join('; ');
      }
    }
    if (Array.isArray(detail)) {
      return detail
        .map((item) =>
          typeof item === 'object' && item && 'msg' in item
            ? String((item as { msg: unknown }).msg)
            : String(item),
        )
        .join('; ');
    }
  }
  return err instanceof Error ? err.message : 'Request failed';
}

function summarizeStatus(label: string, status: AsyncJobStatus): string {
  const result = (status.result || {}) as Record<string, unknown>;
  const lines = [
    `${label}: ${status.status} (${status.phase}) ${status.progress_percent ?? 0}%`,
    status.message,
  ];
  const extras = [
    ['fetched_rows', status.fetched_rows ?? status.fetched_items ?? result.fetched_rows],
    ['matched', status.matched ?? result.matched],
    ['copies_updated', status.copies_updated ?? result.copies_updated],
    ['variants_updated', status.variants_updated ?? result.variants_updated],
    ['would_stamp', status.would_stamp ?? result.would_stamp],
    ['sell_base_would_change', status.sell_base_would_change ?? result.sell_base_would_change],
    ['skipped_no_signals', status.skipped_no_signals ?? result.skipped_no_signals],
    ['override_holds', status.override_holds ?? result.override_holds],
    ['preview_id', status.preview_id ?? result.preview_id],
    ['products_updated', status.products_updated ?? result.products_updated],
    ['products_considered', status.products_considered ?? result.products_considered],
    ['pricing_events_emitted', status.pricing_events_emitted ?? result.pricing_events_emitted],
    ['lows_updated', status.lows_updated ?? result.lows_updated],
    ['sales_rows_written', status.sales_rows_written ?? result.sales_rows_written],
    ['rows_upserted', status.rows_upserted ?? result.rows_upserted],
    ['rows_skipped', status.rows_skipped ?? result.rows_skipped],
  ] as const;
  for (const [key, value] of extras) {
    if (value != null && value !== '') {
      lines.push(`${key}: ${String(value)}`);
    }
  }
  if (status.last_error) {
    lines.push(`error: ${status.last_error}`);
  }
  const when = status.finished_at || status.started_at;
  if (when) {
    lines.push(
      `${status.finished_at ? 'finished_at' : 'started_at'}: ${formatInstant(when, 'never')}`,
    );
  }
  return lines.join('\n');
}

function formatSellerSession(status: TcgplayerSellerSessionStatus): string {
  const lines = [
    `TCG seller session: ${status.status}`,
    `updated_at: ${formatInstant(status.updated_at, 'never')}`,
    `stale: ${status.stale ? 'yes' : 'no'}`,
  ];
  if (status.stale_reason) {
    lines.push(`reason: ${status.stale_reason}`);
  }
  if (status.source) {
    lines.push(`source: ${status.source}`);
  }
  return lines.join('\n');
}

function jobBadgeTone(
  status: AsyncJobStatus | null,
): 'idle' | 'active' | 'success' | 'failed' {
  if (!status) return 'idle';
  const s = String(status.status || '').toLowerCase();
  if (isAsyncJobActive(s)) return 'active';
  if (isAsyncJobFailed(s)) return 'failed';
  if (isAsyncJobSuccess(s)) return 'success';
  return 'idle';
}

function shouldShowJobStatus(status: AsyncJobStatus | null): boolean {
  if (!status) return false;
  const s = String(status.status || '').toLowerCase();
  if (!s || s === 'idle') return false;
  return true;
}

export function PricingSyncControls() {
  const [busy, setBusy] = useState<ActionKey | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  const [jobStatuses, setJobStatuses] = useState<Partial<Record<JobKey, AsyncJobStatus>>>({});
  const [sellerSession, setSellerSession] = useState<TcgplayerSellerSessionStatus | null>(null);
  const [sellerSessionDetail, setSellerSessionDetail] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<PolicyPreviewRowsPage | null>(null);
  const [rebaseSetName, setRebaseSetName] = useState('');
  const [rebasePackCost, setRebasePackCost] = useState('');
  const [rebaseCardsPerPack, setRebaseCardsPerPack] = useState('15');
  const [rebaseBusy, setRebaseBusy] = useState(false);
  const [rebaseResult, setRebaseResult] = useState<AcquisitionPackRebaseResult | null>(null);
  const pollingRef = useRef<Partial<Record<JobKey, boolean>>>({});

  const setJobStatus = useCallback((key: JobKey, status: AsyncJobStatus) => {
    setJobStatuses((prev) => ({ ...prev, [key]: status }));
  }, []);

  const refreshSellerSession = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) {
      setBusy('tcgStatus');
      setActionError(null);
    }
    try {
      const status = await fetchTcgplayerSellerSessionStatus();
      setSellerSession(status);
      if (!silent) {
        setSellerSessionDetail(formatSellerSession(status));
      }
    } catch (err) {
      if (!silent) {
        setActionError(formatApiError(err));
      }
    } finally {
      if (!silent) {
        setBusy(null);
      }
    }
  }, []);

  const resumePoll = useCallback(
    async (key: JobKey, fetchStatus: () => Promise<AsyncJobStatus>) => {
      if (pollingRef.current[key]) return;
      pollingRef.current[key] = true;
      setBusy(key);
      try {
        const finalStatus = await pollAsyncJobStatus(fetchStatus, {
          intervalMs: 2000,
          onUpdate: (status) => setJobStatus(key, status),
        });
        setJobStatus(key, finalStatus);
      } catch (err) {
        setActionError(formatApiError(err));
      } finally {
        pollingRef.current[key] = false;
        setBusy((current) => (current === key ? null : current));
      }
    },
    [setJobStatus],
  );

  const loadPreviewRows = useCallback(async () => {
    try {
      const page = await fetchPolicyPricingPreviewRows({
        skip: 0,
        limit: 20000,
        changedOnly: true,
      });
      setPreviewRows(page);
    } catch {
      setPreviewRows(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const hydrate = async () => {
      await refreshSellerSession({ silent: true });
      const [policyPreview, policy, preferred, listing, market, tcgExport] = await Promise.all([
        fetchPolicyPricingPreviewStatus().catch(() => null),
        fetchPolicyPricingStatus().catch(() => null),
        fetchPreferredRefreshStatus().catch(() => null),
        fetchManapoolListingSyncStatus().catch(() => null),
        fetchManapoolMarketIntelStatus().catch(() => null),
        fetchTcgplayerPricingExportStatus().catch(() => null),
      ]);
      if (cancelled) return;

      const next: Partial<Record<JobKey, AsyncJobStatus>> = {};
      if (policyPreview) next.policyPreview = policyPreview;
      if (policy) next.policy = policy;
      if (preferred) next.preferred = preferred;
      if (listing) next.listing = listing;
      if (market) next.market = market;
      if (tcgExport) next.tcgExport = tcgExport;
      setJobStatuses(next);

      if (policyPreview && isAsyncJobSuccess(String(policyPreview.status || ''))) {
        await loadPreviewRows();
      }

      const active: Array<{ key: JobKey; fetch: () => Promise<AsyncJobStatus> }> = [];
      if (policyPreview && isAsyncJobActive(String(policyPreview.status || ''))) {
        active.push({ key: 'policyPreview', fetch: fetchPolicyPricingPreviewStatus });
      }
      if (policy && isAsyncJobActive(String(policy.status || ''))) {
        active.push({ key: 'policy', fetch: fetchPolicyPricingStatus });
      }
      if (preferred && isAsyncJobActive(String(preferred.status || ''))) {
        active.push({ key: 'preferred', fetch: fetchPreferredRefreshStatus });
      }
      if (listing && isAsyncJobActive(String(listing.status || ''))) {
        active.push({ key: 'listing', fetch: fetchManapoolListingSyncStatus });
      }
      if (market && isAsyncJobActive(String(market.status || ''))) {
        active.push({ key: 'market', fetch: fetchManapoolMarketIntelStatus });
      }
      if (tcgExport && isAsyncJobActive(String(tcgExport.status || ''))) {
        active.push({ key: 'tcgExport', fetch: fetchTcgplayerPricingExportStatus });
      }
      // Resume at most one active poll at a time (buttons already serialize busy).
      for (const job of active) {
        if (cancelled) break;
        await resumePoll(job.key, job.fetch);
        if (job.key === 'policyPreview' && !cancelled) {
          await loadPreviewRows();
        }
      }
    };

    void hydrate();
    return () => {
      cancelled = true;
    };
  }, [refreshSellerSession, resumePoll, loadPreviewRows]);

  const runServerJob = async (
    key: JobKey,
    label: string,
    start: () => Promise<{ accepted: boolean; message: string }>,
    fetchStatus: () => Promise<AsyncJobStatus>,
  ): Promise<AsyncJobStatus | null> => {
    if (pollingRef.current[key]) return null;
    setBusy(key);
    setActionError(null);
    setJobStatus(key, {
      status: 'accepted',
      phase: 'queued',
      message: `${label}: starting on server…`,
      progress_percent: 0,
    });
    pollingRef.current[key] = true;
    try {
      const accepted = await start();
      setJobStatus(key, {
        status: 'accepted',
        phase: 'queued',
        message: accepted.message,
        progress_percent: 0,
      });
      const finalStatus = await pollAsyncJobStatus(fetchStatus, {
        intervalMs: 2000,
        onUpdate: (status) => setJobStatus(key, status),
      });
      setJobStatus(key, finalStatus);
      if (isAsyncJobFailed(String(finalStatus.status || ''))) {
        throw new Error(finalStatus.last_error || finalStatus.message || `${label} failed`);
      }
      return finalStatus;
    } catch (err) {
      setActionError(formatApiError(err));
      return null;
    } finally {
      pollingRef.current[key] = false;
      setBusy(null);
    }
  };

  const runTcgExport = async () => {
    await runServerJob(
      'tcgExport',
      'TCG pricing export',
      () => runTcgplayerPricingExport(),
      () => fetchTcgplayerPricingExportStatus(),
    );
    try {
      const status = await fetchTcgplayerSellerSessionStatus();
      setSellerSession(status);
    } catch {
      // ignore secondary status failure
    }
  };

  const runPolicyPreview = async () => {
    const finalStatus = await runServerJob(
      'policyPreview',
      'Policy preview',
      () => previewPolicyPrices(),
      () => fetchPolicyPricingPreviewStatus(),
    );
    if (finalStatus && isAsyncJobSuccess(String(finalStatus.status || ''))) {
      await loadPreviewRows();
    } else {
      setPreviewRows(null);
    }
  };

  const runPolicyApply = async (previewId: string, adjustments: PolicyApplyAdjustment[]) => {
    await runServerJob(
      'policy',
      'Policy apply',
      () => applyPolicyPrices(previewId, adjustments),
      () => fetchPolicyPricingStatus(),
    );
  };

  const discardPolicyPreviewRun = async () => {
    setActionError(null);
    try {
      const result = await discardPolicyPreview();
      setPreviewRows(null);
      setJobStatus('policyPreview', {
        status: 'idle',
        phase: 'idle',
        message: result.message,
        progress_percent: 0,
        result: null,
      });
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const runAcquisitionRebase = async (dryRun: boolean) => {
    const setName = rebaseSetName.trim();
    const packCost = Number(rebasePackCost);
    const cards = Math.floor(Number(rebaseCardsPerPack) || 15);
    if (!setName) {
      setActionError('Set name is required for acquisition rebase.');
      return;
    }
    if (!Number.isFinite(packCost) || packCost <= 0) {
      setActionError('Pack cost must be a positive dollar amount.');
      return;
    }
    if (!Number.isFinite(cards) || cards < 1) {
      setActionError('Cards per pack must be at least 1.');
      return;
    }
    if (
      !dryRun &&
      !window.confirm(
        `Apply acquisition cost $${(packCost / cards).toFixed(2)} ` +
          `($${packCost.toFixed(2)} / ${cards}) to all sellable copies matching set "${setName}"?`,
      )
    ) {
      return;
    }
    setRebaseBusy(true);
    setActionError(null);
    try {
      const result = await rebaseAcquisitionPackCosts({
        set_name: setName,
        pack_cost: packCost,
        cards_per_pack: cards,
        dry_run: dryRun,
      });
      setRebaseResult(result);
      setActionNotice(result.message);
    } catch (err) {
      setActionError(formatApiError(err));
    } finally {
      setRebaseBusy(false);
    }
  };

  const styles = {
    section: {
      marginBottom: spacing['2xl'],
    },
    sectionTitle: {
      fontSize: typography.h2.fontSize,
      fontWeight: typography.h2.fontWeight,
      color: colors.text,
      marginBottom: spacing.sm,
    },
    helper: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.md,
      maxWidth: '720px',
    },
    list: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: spacing.md,
    },
    row: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: spacing.md,
      paddingTop: spacing.md,
      paddingBottom: spacing.md,
      borderTop: `1px solid ${colors.border}`,
    },
    copy: {
      flex: '1 1 280px',
      minWidth: 0,
    },
    label: {
      fontSize: typography.body.fontSize,
      fontWeight: typographyFlat.fontWeight.medium,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    description: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
    },
    rebaseFields: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: spacing.md,
      marginTop: spacing.sm,
    },
    rebaseField: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: spacing.xs,
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
    },
    rebaseInput: {
      minWidth: '120px',
      padding: `${spacing.xs} ${spacing.sm}`,
      border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.sm,
      fontSize: typography.body.fontSize,
      color: colors.text,
      backgroundColor: colors.white,
    },
    rebaseSummary: {
      marginTop: spacing.sm,
      fontSize: typography.small.fontSize,
      color: colors.text,
    },
    buttonRow: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: spacing.sm,
    },
    button: {
      padding: `${spacing.sm} ${spacing.md}`,
      backgroundColor: colors.primary,
      color: colors.white,
      border: 'none',
      borderRadius: borderRadius.sm,
      cursor: 'pointer',
      fontSize: typography.body.fontSize,
      fontWeight: 500,
      whiteSpace: 'nowrap' as const,
    },
    buttonSecondary: {
      backgroundColor: colors.gray200,
      color: colors.text,
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    badge: {
      display: 'inline-block',
      marginTop: spacing.xs,
      padding: `${spacing.xs} ${spacing.sm}`,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      fontWeight: typographyFlat.fontWeight.medium,
    },
    badgeIdle: {
      backgroundColor: colors.gray100,
      color: colors.textSecondary,
    },
    badgeActive: {
      backgroundColor: `${colors.primary}19`,
      color: colors.primary,
    },
    badgeFresh: {
      backgroundColor: `${colors.success}19`,
      color: colors.success,
    },
    badgeStale: {
      backgroundColor: colors.errorLight,
      color: colors.error,
    },
    jobStatus: {
      marginTop: spacing.sm,
      padding: spacing.sm,
      backgroundColor: colors.gray100,
      color: colors.text,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      whiteSpace: 'pre-wrap' as const,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    jobStatusFailed: {
      backgroundColor: colors.errorLight,
      color: colors.error,
    },
    error: {
      marginTop: spacing.md,
      padding: spacing.md,
      backgroundColor: colors.errorLight,
      color: colors.error,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      whiteSpace: 'pre-wrap' as const,
    },
    notice: {
      marginTop: spacing.md,
      padding: spacing.md,
      backgroundColor: colors.gray100,
      color: colors.success,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      whiteSpace: 'pre-wrap' as const,
    },
    result: {
      marginTop: spacing.md,
      padding: spacing.md,
      backgroundColor: colors.gray100,
      color: colors.text,
      borderRadius: borderRadius.sm,
      fontSize: typography.small.fontSize,
      whiteSpace: 'pre-wrap' as const,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    },
    deltaTableWrap: {
      marginTop: spacing.md,
      overflowX: 'auto' as const,
      width: '100%',
    },
    deltaMeta: {
      fontSize: typography.small.fontSize,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    deltaTable: {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: typography.small.fontSize,
    },
    th: {
      textAlign: 'left' as const,
      padding: `${spacing.xs} ${spacing.sm}`,
      borderBottom: `1px solid ${colors.border}`,
      color: colors.textSecondary,
      fontWeight: typographyFlat.fontWeight.medium,
      whiteSpace: 'nowrap' as const,
    },
    td: {
      padding: `${spacing.xs} ${spacing.sm}`,
      borderBottom: `1px solid ${colors.border}`,
      color: colors.text,
      whiteSpace: 'nowrap' as const,
    },
  };

  const anyBusy = busy !== null;
  const sessionFresh = sellerSession?.status === 'fresh' && !sellerSession?.stale;
  const previewStatus = jobStatuses.policyPreview ?? null;
  const previewReady = isAsyncJobSuccess(String(previewStatus?.status || ''));
  const previewStatusSummary = useMemo(() => {
    const result = previewStatus?.result;
    if (result && typeof result === 'object' && !Array.isArray(result)) {
      return result as Record<string, unknown>;
    }
    return null;
  }, [previewStatus?.result]);

  const renderJobStatus = (key: JobKey) => {
    const status = jobStatuses[key] ?? null;
    if (!shouldShowJobStatus(status)) return null;
    const tone = jobBadgeTone(status);
    const badgeStyle =
      tone === 'failed'
        ? styles.badgeStale
        : tone === 'success'
          ? styles.badgeFresh
          : tone === 'active'
            ? styles.badgeActive
            : styles.badgeIdle;
    const when = status?.finished_at || status?.started_at;
    return (
      <>
        <span style={{ ...styles.badge, ...badgeStyle }}>
          Last run: {status?.status}
          {status?.progress_percent != null ? ` · ${status.progress_percent}%` : ''}
          {when ? ` · ${when}` : ''}
        </span>
        <pre
          style={{
            ...styles.jobStatus,
            ...(tone === 'failed' ? styles.jobStatusFailed : {}),
          }}
        >
          {summarizeStatus(JOB_LABELS[key], status!)}
        </pre>
      </>
    );
  };

  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>Pricing Sync</div>
      <p style={styles.helper}>
        These jobs run on the server and report progress here — the browser request only starts
        the job (no 30s client timeout). Last run status is loaded from the server when you open
        this page (survives navigation). <strong>Preview Policy Delta</strong> shows what would
        stamp, then <strong>Apply Preview</strong> writes those quotes and fans out to Shopify and
        Manapool. Listing sync stamps Manapool listings for intelligence only. Condition markets
        for list parse: Manapool lows and/or TCG Seller Portal Custom Export (see ops doc).
      </p>

      <div style={styles.list}>
        <div style={styles.row}>
          <div style={styles.copy}>
            <div style={styles.label}>Policy pricing</div>
            <div style={styles.description}>
              Preview computes policy_price from TCG Market, TCG Low, Manapool Market, and
              Manapool Low (max of what is fresh; age blend toward Low after 30/60/90 days). Apply
              stamps that stored delta and fans out to Shopify and Manapool. Overrides still win.
              Scheduler default is off.
            </div>
            {renderJobStatus('policyPreview')}
            {renderJobStatus('policy')}
            <PolicyPreviewPanel
              preview={previewRows}
              previewReady={previewReady}
              previewBusy={busy === 'policyPreview'}
              applyBusy={busy === 'policy'}
              statusSummary={previewStatusSummary}
              onPreview={() => void runPolicyPreview()}
              onApply={(previewId, adjustments) => void runPolicyApply(previewId, adjustments)}
              onDiscard={() => void discardPolicyPreviewRun()}
              onOverridesCleared={(message) => {
                if (/^failed/i.test(message) || /fail/i.test(message)) {
                  setActionError(message);
                  setActionNotice(null);
                  return;
                }
                setActionError(null);
                setActionNotice(`${message} Re-run Preview Policy Delta.`);
                setPreviewRows(null);
                setJobStatus('policyPreview', {
                  status: 'idle',
                  phase: 'idle',
                  message: 'Overrides cleared. Run Preview Policy Delta for a fresh run.',
                  progress_percent: 0,
                  result: null,
                });
              }}
            />
          </div>
        </div>

        <div style={{ ...styles.row, borderBottom: `1px solid ${colors.border}` }}>
          <div style={styles.copy}>
            <div style={styles.label}>Rebase acquisition costs (pack open)</div>
            <div style={styles.description}>
              Set sellable-copy acquisition cost to pack cost ÷ cards per pack for a set name
              match (ILIKE). Use for sealed opens (e.g. Hobbit) where market-% cost is wrong.
              Dry-run first. Does not fan out channel prices.
            </div>
            <div style={styles.rebaseFields}>
              <label style={styles.rebaseField}>
                Set name
                <input
                  type="text"
                  value={rebaseSetName}
                  onChange={(e) => setRebaseSetName(e.target.value)}
                  placeholder="e.g. Hobbit"
                  style={styles.rebaseInput}
                  disabled={rebaseBusy || anyBusy}
                />
              </label>
              <label style={styles.rebaseField}>
                Pack cost ($)
                <input
                  type="text"
                  inputMode="decimal"
                  value={rebasePackCost}
                  onChange={(e) => setRebasePackCost(e.target.value)}
                  placeholder="12.00"
                  style={styles.rebaseInput}
                  disabled={rebaseBusy || anyBusy}
                />
              </label>
              <label style={styles.rebaseField}>
                Cards / pack
                <input
                  type="text"
                  inputMode="numeric"
                  value={rebaseCardsPerPack}
                  onChange={(e) => setRebaseCardsPerPack(e.target.value)}
                  style={styles.rebaseInput}
                  disabled={rebaseBusy || anyBusy}
                />
              </label>
            </div>
            {rebaseResult && (
              <div style={styles.rebaseSummary}>
                {rebaseResult.copies_matched} copies · new cost $
                {rebaseResult.new_acquisition_cost.toFixed(2)}
                {rebaseResult.old_avg_cost != null
                  ? ` · old avg $${rebaseResult.old_avg_cost.toFixed(2)}`
                  : ''}
                {rebaseResult.old_min_cost != null && rebaseResult.old_max_cost != null
                  ? ` (${rebaseResult.old_min_cost.toFixed(2)}–${rebaseResult.old_max_cost.toFixed(2)})`
                  : ''}
              </div>
            )}
          </div>
          <div style={styles.buttonRow}>
            <button
              type="button"
              style={{
                ...styles.button,
                ...styles.buttonSecondary,
                ...(rebaseBusy || anyBusy ? styles.buttonDisabled : {}),
              }}
              disabled={rebaseBusy || anyBusy}
              onClick={() => void runAcquisitionRebase(true)}
            >
              {rebaseBusy ? 'Working…' : 'Dry run'}
            </button>
            <button
              type="button"
              style={{
                ...styles.button,
                ...(rebaseBusy || anyBusy ? styles.buttonDisabled : {}),
              }}
              disabled={rebaseBusy || anyBusy}
              onClick={() => void runAcquisitionRebase(false)}
            >
              Apply rebase
            </button>
          </div>
        </div>

        <div style={styles.row}>
          <div style={styles.copy}>
            <div style={styles.label}>Refresh preferred prices</div>
            <div style={styles.description}>
              Recompute MTGJSON preferred guide prices (ladder fallback only). Does not push
              Shopify/Manapool — run Policy Pricing for channel fan-out.
            </div>
            {renderJobStatus('preferred')}
          </div>
          <button
            type="button"
            style={{
              ...styles.button,
              ...(anyBusy ? styles.buttonDisabled : {}),
            }}
            disabled={anyBusy}
            onClick={() =>
              void runServerJob(
                'preferred',
                'Preferred refresh',
                () => refreshPreferredPrices(),
                () => fetchPreferredRefreshStatus(),
              )
            }
          >
            {busy === 'preferred' ? 'Running…' : 'Refresh Preferred'}
          </button>
        </div>

        <div style={styles.row}>
          <div style={styles.copy}>
            <div style={styles.label}>Sync Manapool listing prices</div>
            <div style={styles.description}>
              Pull your seller inventory prices into SyncBase (copies + variants). No qty
              create/delete. Runs fully server-side with live progress.
            </div>
            {renderJobStatus('listing')}
          </div>
          <button
            type="button"
            style={{
              ...styles.button,
              ...(anyBusy ? styles.buttonDisabled : {}),
            }}
            disabled={anyBusy}
            onClick={() =>
              void runServerJob(
                'listing',
                'Listing sync',
                () => syncManapoolListingPrices(),
                () => fetchManapoolListingSyncStatus(),
              )
            }
          >
            {busy === 'listing' ? 'Running…' : 'Sync Listing Prices'}
          </button>
        </div>

        <div style={styles.row}>
          <div style={styles.copy}>
            <div style={styles.label}>Sync Manapool market intel</div>
            <div style={styles.description}>
              Downloads Manapool&apos;s daily public singles catalog on the RQ worker and stamps
              condition×finish lows for matched products, plus recent sales (up to{' '}
              {MARKET_INTEL_PRODUCT_LIMIT} in-stock products). No seller API calls. Does not
              overwrite preferred or listing prices. List parse prefers these lows for LP/MP when
              fresh.
            </div>
            {renderJobStatus('market')}
          </div>
          <button
            type="button"
            style={{
              ...styles.button,
              ...(anyBusy ? styles.buttonDisabled : {}),
            }}
            disabled={anyBusy}
            onClick={() =>
              void runServerJob(
                'market',
                'Market intel',
                () => syncManapoolMarketIntel(MARKET_INTEL_PRODUCT_LIMIT),
                () => fetchManapoolMarketIntelStatus(),
              )
            }
          >
            {busy === 'market' ? 'Running…' : 'Sync Market Intel'}
          </button>
        </div>

        <div style={{ ...styles.row, borderBottom: `1px solid ${colors.border}` }}>
          <div style={styles.copy}>
            <div style={styles.label}>TCGPlayer seller session + pricing export</div>
            <div style={styles.description}>
              Extension pushes Seller Portal cookies into Vault. Then run Custom Export here to
              ingest condition-level TCG markets for list parse. Keep a Seller Portal tab logged
              in; see <code>docs/operations/CONDITION_PRICING_OPS.md</code>.
            </div>
            {sellerSession && (
              <span
                style={{
                  ...styles.badge,
                  ...(sessionFresh ? styles.badgeFresh : styles.badgeStale),
                }}
              >
                Session: {sellerSession.status}
                {sellerSession.updated_at
                  ? ` · ${formatInstant(sellerSession.updated_at)}`
                  : ''}
              </span>
            )}
            {renderJobStatus('tcgExport')}
          </div>
          <div style={styles.buttonRow}>
            <button
              type="button"
              style={{
                ...styles.button,
                ...styles.buttonSecondary,
                ...(anyBusy ? styles.buttonDisabled : {}),
              }}
              disabled={anyBusy}
              onClick={() => void refreshSellerSession()}
            >
              {busy === 'tcgStatus' ? 'Checking…' : 'Refresh Session'}
            </button>
            <button
              type="button"
              style={{
                ...styles.button,
                ...(anyBusy || !sessionFresh ? styles.buttonDisabled : {}),
              }}
              disabled={anyBusy || !sessionFresh}
              onClick={() => void runTcgExport()}
              title={
                sessionFresh
                  ? 'Download Pricing Custom Export and ingest condition markets'
                  : 'Push a fresh session from the browser extension first'
              }
            >
              {busy === 'tcgExport' ? 'Exporting…' : 'Run TCG Pricing Export'}
            </button>
          </div>
        </div>
      </div>

      {actionNotice && <div style={styles.notice}>{actionNotice}</div>}
      {actionError && <div style={styles.error}>{actionError}</div>}
      {sellerSessionDetail && <pre style={styles.result}>{sellerSessionDetail}</pre>}
    </div>
  );
}
